import {
  Activity,
  Award,
  Boxes,
  Briefcase,
  ShoppingCart,
  CalendarClock,
  CalendarDays,
  Factory,
  ClipboardCheck,
  ClipboardList,
  Database,
  FileSignature,
  FileSpreadsheet,
  FileText,
  GraduationCap,
  HardHat,
  History,
  Inbox,
  ListChecks,
  Mail,
  MailWarning,
  Plug,
  Printer,
  ScanLine,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Truck,
  UserCog,
  UserPlus,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { hasStaffRole } from '@hcc/types';
import type { SessionUser } from '../../hooks/useAuth';

/**
 * Single source of truth for the role-gated nav. Both the desktop
 * TopNav (groups → dropdowns) and the MobileNavDrawer (groups →
 * collapsible sections) consume this so the two stay in lockstep.
 *
 * Leaves are direct links; groups expand into a menu / section.
 */

const ADMIN = ['admin', 'super_admin'];
const COORD = ['admin', 'super_admin', 'coordinator'];
const HR_LIKE = ['admin', 'super_admin', 'coordinator', 'hr'];
// Onboarding administration: admins plus the dedicated onboarding role.
const ONBOARDING_ADMIN = ['admin', 'super_admin', 'onboarding'];
// Onboarding operations (dashboard + submissions): the admins above plus HR.
// HR runs the cohort but does not edit templates, forms or training content.
const ONBOARDING_OPS = [...ONBOARDING_ADMIN, 'hr'];
// The guided new-employee flow needs all three of: create a person
// (createGate), assign onboarding content (ONBOARDING_ACCESS_ROLES) and read
// the person record it finishes on (editGate). Only these roles hold all
// three - a coordinator cannot assign, and the onboarding role cannot read a
// person record. Exported so the route gate cannot drift from the nav.
const NEW_EMPLOYEE = ['admin', 'super_admin', 'hr'];
const STATION_ROLES = [
  'station_it',
  'station_ppe',
  'station_tools',
  'station_fleet',
  // station_dbshr intentionally absent — see STATIONS comment below.
];

function hasAny(user: SessionUser, allowed: readonly string[]): boolean {
  return user.roles.some((r) => allowed.includes(r));
}

/** Staff = anything beyond the self-service roles (inductee, learner). */
function isStaff(user: SessionUser): boolean {
  return hasStaffRole(user.roles);
}

export interface NavLeaf {
  kind: 'leaf';
  to: string;
  label: string;
  icon: LucideIcon;
  description?: string;
  /**
   * Live counter shown beside the label. 'task-reviews' = awaiting-review
   * count; 'dbs-checks' = DBS applications pending HR review.
   */
  badge?: 'task-reviews' | 'dbs-checks';
  /**
   * Match the active state exactly, not by prefix. Needed for a landing path
   * that is a prefix of a sibling (e.g. /onboarding vs /onboarding/submissions)
   * so both don't highlight at once.
   */
  exact?: boolean;
}

export interface NavGroup {
  kind: 'group';
  label: string;
  icon?: LucideIcon;
  items: NavLeaf[];
}

export type NavEntry = NavLeaf | NavGroup;

export interface NavContext {
  /**
   * True when the current scope is the durable day-to-day onboarding
   * programme (kind='onboarding'). Hides the mobilisation-day-only nav
   * (reception, stations, coordinator, roll-call register, printable
   * fallback, post-day sends) and relabels the kit group so the sidebar
   * reads as day-to-day onboarding rather than a station-day. A dated
   * mobilisation or the "All mobilisations" view keeps the full nav.
   */
  dayToDay: boolean;
}

// Driven by VISIBLE_STATIONS — DBSHR is intentionally absent until
// the HR team has a concrete use case. Add it back to VISIBLE_STATIONS
// in @hcc/types to restore the nav entry.
const STATIONS: { id: string; label: string; icon: LucideIcon; role: string }[] = [
  { id: 'IT', label: 'IT', icon: ScanLine, role: 'station_it' },
  { id: 'PPE', label: 'PPE / Uniform', icon: HardHat, role: 'station_ppe' },
  { id: 'Tools', label: 'Tools', icon: Wrench, role: 'station_tools' },
  { id: 'Fleet', label: 'Fleet', icon: Truck, role: 'station_fleet' },
];

export function buildNavSections(
  user: SessionUser,
  ctx: NavContext = { dayToDay: false },
): NavEntry[] {
  const { dayToDay } = ctx;
  const out: NavEntry[] = [];

  // On the day. Hidden day-to-day: a Monday starter is not checked in at a
  // reception desk.
  if (!dayToDay && (user.roles.includes('reception') || hasAny(user, COORD))) {
    out.push({
      kind: 'leaf',
      to: '/reception',
      label: 'Reception',
      icon: ClipboardList,
    });
  }

  const visibleStations = hasAny(user, COORD)
    ? STATIONS
    : STATIONS.filter((s) => user.roles.includes(s.role));

  // Per-station desks are a mobilisation-day concept; day-to-day issues kit
  // directly via "Issue kit" below.
  if (!dayToDay && visibleStations.length > 0) {
    out.push({
      kind: 'group',
      label: 'Stations',
      icon: HardHat,
      items: visibleStations.map((s) => ({
        kind: 'leaf' as const,
        to: `/stations/${s.id}`,
        label: s.label,
        icon: s.icon,
      })),
    });
  }

  if (!dayToDay && hasAny(user, COORD)) {
    out.push({
      kind: 'leaf',
      to: '/coordinator',
      label: 'Coordinator',
      icon: Activity,
    });
  }

  // Documents and Register are staff-only: the documents hub lists
  // every attendee, and the register is the live roll-call. Inductees
  // see their own data via /me instead. The register is a mobilisation-day
  // roll-call, so it drops out day-to-day; documents stay useful either way.
  if (isStaff(user)) {
    out.push({
      kind: 'leaf',
      to: '/documents',
      label: 'Documents',
      icon: FileSignature,
    });
    if (!dayToDay) {
      out.push({
        kind: 'leaf',
        to: '/register',
        label: 'Register',
        icon: ClipboardCheck,
      });
    }
  }

  if (hasAny(user, HR_LIKE)) {
    out.push({
      kind: 'leaf',
      to: '/reports',
      label: 'Reports',
      icon: ListChecks,
    });
  }

  // Onboarding administration. Its own group so the mobilisation-day
  // leaves above (Documents, Register, Reports) don't visually nest under
  // the ONBOARDING header. Dashboard and Submissions are visible to HR too;
  // the content-builder pages stay admin / onboarding only.
  if (hasAny(user, ONBOARDING_OPS)) {
    const onboardingItems: NavLeaf[] = [
      {
        kind: 'leaf',
        to: '/onboarding',
        label: 'Dashboard',
        icon: Activity,
        description: 'Cohort progress and invites',
        // Otherwise /onboarding/submissions would light up Dashboard too.
        exact: true,
      },
    ];
    if (hasAny(user, NEW_EMPLOYEE)) {
      onboardingItems.push({
        kind: 'leaf',
        to: '/onboarding/new',
        label: 'New employee',
        icon: UserPlus,
        description: 'Add a starter and set up their onboarding in one pass',
      });
    }
    onboardingItems.push(
      {
        kind: 'leaf',
        to: '/onboarding/submissions',
        label: 'Submissions',
        icon: Inbox,
        description: 'View what each person has submitted or started',
      },
    );
    if (hasAny(user, ONBOARDING_ADMIN)) {
      onboardingItems.push(
        {
          kind: 'leaf',
          to: '/admin/onboarding-templates',
          label: 'Onboarding templates',
          icon: FileText,
          description: 'Per-role onboarding task lists',
        },
        {
          kind: 'leaf',
          to: '/admin/onboarding-documents',
          label: 'Documents to sign',
          icon: FileText,
          description: 'Upload and version documents',
        },
        {
          kind: 'leaf',
          to: '/admin/onboarding-forms',
          label: 'Onboarding forms',
          icon: FileSpreadsheet,
          description: 'Build and version the forms inductees fill in',
        },
        {
          kind: 'leaf',
          to: '/admin/onboarding-tasks',
          label: 'Task library',
          icon: ListChecks,
          description: 'Reusable tasks to assign to people',
        },
        {
          kind: 'leaf',
          to: '/admin/training-modules',
          label: 'Training modules',
          icon: GraduationCap,
          description: 'Build e-learning with slides, quizzes and certificates',
        },
      );
    }
    out.push({
      kind: 'group',
      label: 'Onboarding',
      icon: ClipboardList,
      items: onboardingItems,
    });
  }

  // People (HR-like+)
  if (hasAny(user, HR_LIKE)) {
    const peopleItems: NavLeaf[] = [
      {
        kind: 'leaf',
        to: '/admin/people',
        label: 'Browse people',
        icon: Database,
        description: 'Search, edit, GDPR actions',
      },
      {
        kind: 'leaf',
        to: '/admin/task-reviews',
        label: 'Tasks to review',
        icon: ClipboardCheck,
        description: 'Approve / reject inductee submissions',
        badge: 'task-reviews',
      },
      {
        kind: 'leaf',
        to: '/admin/checks/dbs',
        label: 'DBS checks',
        icon: ShieldCheck,
        description: 'Track DBS applications through to the certificate check',
        badge: 'dbs-checks',
      },
      {
        kind: 'leaf',
        to: '/admin/training-progress',
        label: 'Training progress',
        icon: GraduationCap,
        description: 'Who has completed which training module, with scores',
      },
    ];
    if (hasAny(user, COORD)) {
      peopleItems.push({
        kind: 'leaf',
        to: '/admin/import-attendees',
        label: 'Import attendees',
        icon: Users,
        description: 'Bulk CSV upload',
      });
      peopleItems.push({
        kind: 'leaf',
        to: '/admin/mobilisations',
        label: 'Mobilisations',
        icon: CalendarDays,
        description: 'Set up and manage events',
      });
    }
    if (hasAny(user, ADMIN)) {
      peopleItems.push({
        kind: 'leaf',
        to: '/admin/people-system-creation',
        label: 'People System Creation',
        icon: Factory,
        description: 'Push new starters into SAP and Planon, with progress',
      });
    }
    out.push({
      kind: 'group',
      label: 'People',
      icon: Users,
      items: peopleItems,
    });
  }

  // Pre-day — coord+ for templates / sizing; station_* and coord+ for Equipment
  const hasStationRole = user.roles.some((r) => STATION_ROLES.includes(r));
  if (hasAny(user, COORD) || hasStationRole) {
    const preDay: NavLeaf[] = [];
    if (hasAny(user, COORD)) {
      preDay.push({
        kind: 'leaf',
        to: '/admin/kit-templates',
        label: 'Kit templates',
        icon: FileText,
        description: 'Per-role item lists',
      });
    }
    // Issue kit early: coordinators hand out any station's kit before the
    // day; a station user issues their own station's kit early (the picker
    // scopes to the stations they hold a role for). Reception / HR without
    // a station role don't see it.
    if (hasAny(user, COORD) || hasStationRole) {
      preDay.push({
        kind: 'leaf',
        to: '/admin/pre-day-issue',
        // "Early" only means anything relative to a mobilisation day; in
        // day-to-day onboarding this is simply the kit-issue flow.
        label: dayToDay ? 'Issue kit' : 'Issue kit early',
        icon: CalendarClock,
        description: dayToDay
          ? 'Issue kit with signature and PDF receipt'
          : 'Hand out kit before the day',
      });
    }
    if (hasAny(user, COORD)) {
      preDay.push({
        kind: 'leaf',
        to: '/admin/kit-orders',
        label: 'Kit to order',
        icon: ShoppingCart,
        description: 'Uniform / PPE to buy from sizes',
      });
    }
    preDay.push({
      kind: 'leaf',
      to: '/admin/assets',
      label: 'Equipment',
      icon: Boxes,
      description: 'Asset register and pre-assignment',
    });
    // Paper backup for a mobilisation day; not a day-to-day concern.
    if (!dayToDay && hasAny(user, HR_LIKE)) {
      preDay.push({
        kind: 'leaf',
        to: '/admin/printable',
        label: 'Printable fallback',
        icon: Printer,
        description: 'Paper backup list',
      });
    }
    // "Pre-day" is mobilisation framing; day-to-day it is just the kit desk.
    out.push({
      kind: 'group',
      label: dayToDay ? 'Kit & equipment' : 'Pre-day',
      icon: dayToDay ? Boxes : CalendarDays,
      items: preDay,
    });
  }

  // Post-day (coordinator+)
  //
  // Leaver returns are intentionally hidden from the nav for now: the
  // operator asked for it off until the use case lands. The route
  // (/admin/leaver-return) and its backend wiring still exist; restore
  // the leaf below when that flow is wanted again.
  // Bulk post-day sends belong to a mobilisation day, not day-to-day intake.
  if (!dayToDay && hasAny(user, COORD)) {
    const postDay: NavLeaf[] = [
      {
        kind: 'leaf',
        to: '/admin/certificates',
        label: 'Completion certificates',
        icon: Award,
        description: 'Bulk send completion PDFs',
      },
      {
        kind: 'leaf',
        to: '/admin/feedback',
        label: 'Feedback survey',
        icon: Mail,
        description: 'Collect post-day feedback',
      },
    ];
    out.push({
      kind: 'group',
      label: 'Post-day',
      icon: Award,
      items: postDay,
    });
  }

  // System (admin+)
  if (hasAny(user, ADMIN)) {
    out.push({
      kind: 'group',
      label: 'System',
      icon: Settings,
      items: [
        {
          kind: 'leaf',
          to: '/admin/users',
          label: 'Users & roles',
          icon: UserCog,
        },
        {
          kind: 'leaf',
          to: '/admin/activity',
          label: 'Active users',
          icon: Activity,
          description: 'Who is on the site right now',
        },
        {
          kind: 'leaf',
          to: '/admin/failed-logins',
          label: 'Failed sign-ins',
          icon: MailWarning,
          description: 'Unknown emails typed at the sign-in page',
        },
        {
          kind: 'leaf',
          to: '/admin/roles',
          label: 'Job roles',
          icon: Briefcase,
          description: 'Roles people can hold (for onboarding)',
        },
        {
          kind: 'leaf',
          to: '/admin/emergency-access',
          label: 'Emergency log',
          icon: ShieldAlert,
        },
        {
          kind: 'leaf',
          to: '/admin/access-log',
          label: 'Access log',
          icon: History,
        },
        {
          kind: 'leaf',
          to: '/admin/email-log',
          label: 'Email log',
          icon: Mail,
        },
        {
          kind: 'leaf',
          to: '/admin/templates/email',
          label: 'Email templates',
          icon: FileText,
        },
        {
          kind: 'leaf',
          to: '/admin/templates/pdf',
          label: 'PDF templates',
          icon: Award,
        },
        {
          kind: 'leaf',
          to: '/admin/station-settings',
          label: 'Station settings',
          icon: Settings,
        },
        {
          kind: 'leaf',
          to: '/admin/system',
          label: 'System status',
          icon: Settings,
        },
        {
          kind: 'leaf',
          to: '/admin/planon',
          label: 'Planon integration',
          icon: Plug,
        },
        {
          kind: 'leaf',
          to: '/admin/sap',
          label: 'SAP integration',
          icon: Plug,
        },
      ],
    });
  }

  return out;
}

/**
 * True when the user has any primary nav (i.e. is staff). Inductees have an
 * empty nav, so the desktop rail is hidden for them and the brand moves into
 * the TopBar — Sidebar and TopBar share this so they stay in lockstep.
 */
export function hasPrimaryNav(user: SessionUser): boolean {
  return buildNavSections(user).length > 0;
}

/** Returns true if any of the group's leaf paths matches the current pathname. */
export function isGroupActive(group: NavGroup, pathname: string): boolean {
  return group.items.some((l) => pathname === l.to || pathname.startsWith(l.to + '/'));
}

export { ADMIN, COORD, HR_LIKE, NEW_EMPLOYEE, STATION_ROLES, hasAny };
